import { useCallback, useEffect, useState } from 'react'
import type {
  ProjectContextResponse,
  UpdateProjectAiVersionRequest,
  UpdateProjectContextRequest,
} from '@ai-meeting/shared'
import { ApiError } from '@/services/api'
import {
  fetchProjectContext,
  updateProjectAiVersion,
  updateProjectContext,
} from '@/services/projects'
import { useUiStrings } from '@/hooks/useUiStrings'

export type ProjectContextLoadState = 'loading' | 'ready' | 'error'

/**
 * Loads and saves project context for the Context & Dictionary tab.
 * Reloads when `projectKey` changes; mutations replace local state from the PUT response.
 */
export function useProjectContext(projectKey: string) {
  const { t } = useUiStrings()
  const [state, setState] = useState<ProjectContextLoadState>('loading')
  const [context, setContext] = useState<ProjectContextResponse | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const reload = useCallback(async () => {
    if (!projectKey.trim()) {
      setState('error')
      setErrorMessage(t.failedLoadProjectContext)
      setContext(null)
      return
    }

    setState('loading')
    setErrorMessage(null)
    try {
      const data = await fetchProjectContext(projectKey)
      setContext(data)
      setState('ready')
    } catch (err: unknown) {
      setContext(null)
      setState('error')
      setErrorMessage(
        err instanceof ApiError ? err.message : t.failedLoadProjectContext
      )
    }
  }, [projectKey, t.failedLoadProjectContext])

  useEffect(() => {
    let active = true
    setState('loading')
    setErrorMessage(null)

    if (!projectKey.trim()) {
      setState('error')
      setErrorMessage(t.failedLoadProjectContext)
      setContext(null)
      return
    }

    void fetchProjectContext(projectKey)
      .then((data) => {
        if (!active) return
        setContext(data)
        setState('ready')
      })
      .catch((err: unknown) => {
        if (!active) return
        setContext(null)
        setState('error')
        setErrorMessage(
          err instanceof ApiError ? err.message : t.failedLoadProjectContext
        )
      })

    return () => {
      active = false
    }
  }, [projectKey, t.failedLoadProjectContext])

  const save = useCallback(
    async (body: UpdateProjectContextRequest) => {
      setSaving(true)
      setErrorMessage(null)
      try {
        const data = await updateProjectContext(projectKey, body)
        setContext(data)
        setState('ready')
        return data
      } catch (err: unknown) {
        const message =
          err instanceof ApiError ? err.message : t.failedSaveProjectContext
        setErrorMessage(message)
        throw err
      } finally {
        setSaving(false)
      }
    },
    [projectKey, t.failedSaveProjectContext]
  )

  const saveAiVersion = useCallback(
    async (body: UpdateProjectAiVersionRequest) => {
      setSaving(true)
      setErrorMessage(null)
      try {
        const data = await updateProjectAiVersion(projectKey, body)
        setContext(data)
        setState('ready')
        return data
      } catch (err: unknown) {
        const message =
          err instanceof ApiError ? err.message : t.failedSaveProjectAiVersion
        setErrorMessage(message)
        throw err
      } finally {
        setSaving(false)
      }
    },
    [projectKey, t.failedSaveProjectAiVersion]
  )

  return {
    state,
    context,
    errorMessage,
    saving,
    reload,
    save,
    saveAiVersion,
  }
}
